/**
 * VerificationBadge — состояние проверки рядом с общим баллом.
 *
 * До этого состояние жило плашкой во всю ширину над оценками. При доле
 * блокировок 82% (замер #752) она стояла на четырёх записях из пяти и от этого
 * перестала что-либо значить: предупреждение, которое видно всегда, читается
 * как часть рамки, а не как предупреждение.
 *
 * Поэтому здесь только знак — рядом с числом, которое он и уточняет, — а текст
 * приходит по требованию: наведение или фокус дают статус и причины, клик
 * раскрывает разбор под карточкой.
 *
 * Кнопка презентационная: раскрытым состоянием владеет панель, потому что сам
 * разбор рисуется не здесь, а ниже карточки балла. Держать это состояние внутри
 * значка значило бы либо тащить разбор в тесную колонку шапки, либо заводить
 * второго владельца того же флага.
 */

import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AnalysisVerificationState } from '@aica/api-types/schemas';
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip';
import {
  VERIFICATION_DETAIL_ID,
  verificationTitle,
} from '@/lib/records/verification-title';
import { cn } from '@/lib/utils';

export function VerificationBadge({
  verification,
  expanded,
  onToggle,
}: {
  verification: AnalysisVerificationState;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(['records', 'common']);
  const { verificationStatus, reviewReasonCodes } = verification;

  const label = verificationTitle(verificationStatus, (key, fallback) =>
    t(key, fallback)
  );
  const reasons = reviewReasonCodes
    .map((code) => t(`common:reviewQueue.reasons.${code}`, code))
    .join(' · ');

  // Провайдер монтируется здесь, а не наверху приложения: Radix требует его
  // предком КАЖДОГО тултипа (иначе `Tooltip` бросает и роняет рендер всей
  // панели), а глобального провайдера в ui-worker нет. Ставить его в корень
  // ради одного значка — правка чужой области; локальный провайдер стоит
  // ничего и держит компонент самодостаточным.
  return (
    <TooltipProvider>
      <Tooltip
        side="left"
        // `Tooltip` по умолчанию молчит на всём, что умеет касания, — а это и
        // сенсорный ноутбук, и эмуляция устройства в Chrome, и встроенная
        // поверхность Bitrix24. Там от значка оставался бы жёлтый треугольник
        // без единого слова, тогда как доки обещают статус и причины по
        // наведению. Текст остаётся достижим и кликом, но искать его было бы
        // нечем.
        disableOnTouch={false}
        content={
          <div>
            <p className="text-xs font-medium text-fg">{label}</p>
            {reasons && <p className="mt-0.5 text-xs text-fg-muted">{reasons}</p>}
            <p className="mt-1 text-xs text-fg-subtle">
              {t('records.verificationNotice.badgeHint', 'Open for details')}
            </p>
          </div>
        }
      >
        <button
          type="button"
          data-testid="verification-badge"
          aria-expanded={expanded}
          // Без пары к `aria-expanded` читалка сообщает «развёрнуто» и не даёт
          // пути к раскрытому: разбор лежит двумя соседями дальше по дереву.
          aria-controls={VERIFICATION_DETAIL_ID}
          aria-label={label}
          onClick={onToggle}
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            'bg-warning-dim text-warning-fg transition-colors duration-150',
            'hover:bg-warning hover:text-bg',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </Tooltip>
    </TooltipProvider>
  );
}
